import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Calendar,
  Clock,
  User,
  ArrowRight,
  Twitter,
  Linkedin,
  Link2,
} from "lucide-react";
import { toast } from "sonner";
import PageShell from "@/components/site/PageShell";
import { getPostBySlug, BLOG_POSTS } from "@/lib/blog-posts";

const FOUNDER_IMG =
  "https://customer-assets.emergentagent.com/job_echowings-premium/artifacts/zurefzue_image.png";

function BodyBlock({ block }) {
  if (block.type === "h2") {
    return (
      <h2 className="mt-12 mb-5 font-display text-3xl md:text-4xl font-bold tracking-tight">
        {block.text}
      </h2>
    );
  }
  if (block.type === "p") {
    return (
      <p className="text-lg text-white/75 leading-relaxed mb-5">{block.text}</p>
    );
  }
  if (block.type === "list") {
    return (
      <ul className="space-y-3 my-6">
        {block.items.map((it) => (
          <li key={it} className="flex items-start gap-3 text-white/80">
            <span className="mt-2 w-1.5 h-1.5 rounded-full bg-gradient-to-br from-blue-600 to-cyan-400 shrink-0" />
            <span className="leading-relaxed">{it}</span>
          </li>
        ))}
      </ul>
    );
  }
  return null;
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  if (!post) {
    return (
      <PageShell testId="blog-post-not-found">
        <section className="section text-center">
          <h1 className="font-display text-4xl font-bold">
            Article coming soon.
          </h1>
          <p className="mt-4 text-white/55">
            This article isn&apos;t published yet.
          </p>
          <Link
            to="/blog"
            className="mt-8 inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 font-semibold"
          >
            <ArrowLeft className="w-4 h-4" /> Back to all articles
          </Link>
        </section>
      </PageShell>
    );
  }

  const hasBody = Array.isArray(post.body) && post.body.length > 0;
  const related = BLOG_POSTS.filter((p) => p.slug !== slug).slice(0, 3);

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success("Link copied to clipboard.");
  };

  return (
    <PageShell testId="blog-post-page">
      {/* Header */}
      <section className="relative pt-10 pb-10">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <Link
            to="/blog"
            data-testid="blog-post-back"
            className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-cyan-400 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> All Articles
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mt-8"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-300 text-[10px] uppercase tracking-widest font-semibold">
              {post.category}
            </div>
            <h1 className="mt-5 font-display text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              {post.title}
            </h1>
            <p className="mt-5 text-lg text-white/65 leading-relaxed">
              {post.excerpt}
            </p>

            <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-3 text-sm text-white/55">
              <span className="inline-flex items-center gap-1.5">
                <User className="w-3.5 h-3.5 text-cyan-400" />{" "}
                <span className="text-white font-semibold">{post.author}</span>
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5 text-cyan-400" /> {post.date}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5 text-cyan-400" /> {post.readTime}
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Hero image */}
      <section className="pb-12">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="rounded-3xl overflow-hidden border border-white/10">
            <img src={post.image} alt={post.title} className="w-full h-auto" />
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="pb-16">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          {hasBody ? (
            <article
              data-testid="blog-post-body"
              className="font-body"
            >
              {post.body.map((block, i) => (
                <BodyBlock key={i} block={block} />
              ))}
            </article>
          ) : (
            <div className="rounded-3xl border border-white/10 bg-white/[0.025] p-10 text-center">
              <h3 className="font-display text-2xl font-semibold">
                Full article coming soon.
              </h3>
              <p className="mt-3 text-white/55">
                We&apos;re polishing this one. In the meantime, browse the rest
                of our journal below.
              </p>
            </div>
          )}

          {/* Share */}
          <div className="mt-12 pt-8 border-t border-white/10 flex flex-wrap items-center justify-between gap-4">
            <span className="text-xs uppercase tracking-widest text-white/45 font-semibold">
              Share this article
            </span>
            <div className="flex items-center gap-2">
              <a
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="blog-share-twitter"
                aria-label="Share on Twitter"
                className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/70 hover:text-cyan-400 hover:border-cyan-500/40 transition-colors"
              >
                <Twitter className="w-4 h-4" />
              </a>
              <a
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(typeof window !== "undefined" ? window.location.href : "")}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="blog-share-linkedin"
                aria-label="Share on LinkedIn"
                className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/70 hover:text-cyan-400 hover:border-cyan-500/40 transition-colors"
              >
                <Linkedin className="w-4 h-4" />
              </a>
              <button
                onClick={copyLink}
                data-testid="blog-share-copy"
                aria-label="Copy link"
                className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/70 hover:text-cyan-400 hover:border-cyan-500/40 transition-colors"
              >
                <Link2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Author card */}
      <section className="pb-16">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="rounded-3xl border border-white/10 bg-white/[0.025] p-6 md:p-8 flex flex-col sm:flex-row items-start gap-6">
            <img
              src={FOUNDER_IMG}
              alt={post.author}
              className="w-20 h-20 rounded-full object-cover border border-white/10"
            />
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold">
                About the author
              </div>
              <div className="mt-1 font-display text-xl font-bold">
                {post.author}
              </div>
              <div className="text-sm text-white/55">
                {post.authorRole || "Founder & CEO, EchoWings Media"}
              </div>
              <p className="mt-3 text-sm text-white/65 leading-relaxed">
                {post.authorBio ||
                  "Matthew has spent over a decade growing service businesses through search and now leads EchoWings Media's GEO + SEO practice."}
              </p>
              <div className="mt-4">
                <Link
                  to="/book-audit"
                  data-testid="blog-author-cta"
                  className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 font-semibold text-sm"
                >
                  Work with Matthew <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="section pt-0">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between mb-8">
            <h2 className="font-display text-3xl font-bold">Keep reading</h2>
            <Link
              to="/blog"
              className="text-sm text-cyan-400 hover:text-cyan-300 font-semibold"
            >
              All articles →
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {related.map((p, i) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                data-testid={`blog-related-${i}`}
                className="group rounded-3xl border border-white/10 bg-white/[0.025] overflow-hidden hover:border-cyan-500/30 transition-colors"
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                </div>
                <div className="p-5">
                  <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold">
                    {p.category}
                  </div>
                  <h3 className="mt-1 font-display text-lg font-semibold leading-tight">
                    {p.title}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </PageShell>
  );
}
